import { candles } from '~data';
import { valueSeries, type Basis } from '~/stores/series-store';

/**
 * The return over one window, on one basis.
 *
 * Measured from the window's own first close with one unit, rather than sliced
 * out of a running total. That is not a stylistic choice: banked distributions
 * do not compound, so on the cash basis the ratio of two points on a cumulative
 * series is not the return between them. Starting fresh gives the same
 * arithmetic for all three bases and makes them comparable.
 *
 * Null where the window holds fewer than two closes — one price is not a return.
 */
export const periodReturn = (symbol: string, basis: Basis, from: number, to: number): number | null => {
    const within = valueSeries(symbol, basis, from).filter((p) => p.t <= to);
    if (within.length < 2 || within[0].value <= 0) return null;
    return (within[within.length - 1].value / within[0].value - 1) * 100;
};

/** Whether a fund traded at all between two dates, as yyyy-mm-dd strings. */
export const tradedBetween = (symbol: string, from: string, to: string): boolean => candles(symbol).some((c) => c.date >= from && c.date <= to);

/** Calendar years any of these funds has a close in, newest first. */
export const yearsCovering = (symbols: string[]): number[] => {
    const years = new Set<number>();
    for (const symbol of symbols) for (const c of candles(symbol)) years.add(Number(c.date.slice(0, 4)));
    return [...years].sort((a, b) => b - a);
};

/** Calendar months any of these funds has a close in, newest first, as
    `yyyy-mm`. */
export const monthsCovering = (symbols: string[]): string[] => {
    const months = new Set<string>();
    for (const symbol of symbols) for (const c of candles(symbol)) months.add(c.date.slice(0, 7));
    return [...months].sort((a, b) => b.localeCompare(a));
};

/** First and last instant of a `yyyy` or `yyyy-mm` label. */
export const boundsOf = (label: string): { from: number; to: number; start: string; end: string } => {
    const start = label.length === 4 ? `${label}-01-01` : `${label}-01`;
    const end = label.length === 4 ? `${label}-12-31` : `${label}-31`;
    return { from: Date.parse(start), to: Date.parse(end), start, end };
};
